// Intrarea în comunitate: un formular cu trei stări — intrare, înregistrare, recuperare.
//
// Local, prima deschidere n-are niciun cont; atunci formularul pornește pe „înregistrare",
// fiindcă n-ai cu ce să te autentifici. Primul cont creat devine proprietar și e
// considerat confirmat din start: altfel n-ar avea cine să citească linkurile de
// confirmare cât timp SMTP-ul nu e configurat.

import { el, clear } from '../core/util.js';
import { icon } from './icons.js';
import { session } from '../core/session.js';
import { modal, toast, passwordField } from './components.js';

export function openAuth({ mode = null } = {}) {
  let regim = mode || (session.empty ? 'register' : 'login');

  const userEl = el('input', {
    class: 'auth-input', type: 'text', placeholder: 'Ник',
    autocomplete: 'username', name: 'username',
  });
  // `type=email` scoate tastatura cu „@" pe telefon, `autocomplete=email` face sistemul
  // sa propuna adresa contului de pe aparat, iar `name` ajuta managerele mai vechi.
  // Toate trei sunt necesare, si niciuna nu e destul singura — dar nimic nu functioneaza
  // pana campurile nu stau intr-un `<form>`: fara el, browserul nici nu se uita.
  const emailEl = el('input', {
    class: 'auth-input', type: 'email', placeholder: 'Почта',
    autocomplete: 'email', name: 'email', inputmode: 'email',
    autocapitalize: 'off', spellcheck: 'false',
  });
  const { field: passField, input: passEl } = passwordField('Пароль');
  const { field: pass2Field, input: pass2El } = passwordField('Повторите пароль', 'new-password');

  const userField = el('label', { class: 'auth-field' }, [icon('user', 13), userEl]);
  const emailField = el('label', { class: 'auth-field' }, [icon('send', 13), emailEl]);

  // Acordul e o bifă, nu o notă de subsol: dacă cerem acceptarea unor reguli, omul
  // trebuie să le poată citi înainte, nu după.
  const acordEl = el('input', { type: 'checkbox', class: 'auth-check' });
  const acordField = el('label', { class: 'auth-agree' }, [
    acordEl,
    el('span', {}, [
      el('span', { text: 'Я принимаю ' }),
      el('a', {
        class: 'auth-link', href: '/rules/', target: '_blank', rel: 'noopener',
        text: 'правила сообщества и политику данных',
      }),
    ]),
  ]);

  const errEl = el('div', { class: 'auth-error' });
  const titleEl = el('h3', { class: 'auth-title' });
  const hintEl = el('p', { class: 'auth-hint' });
  const submitEl = el('button', { class: 'btn primary auth-submit', type: 'submit' });
  // Fara `type=button`, orice buton dintr-un formular il trimite.
  const switchEl = el('button', { class: 'auth-switch', type: 'button' });
  const forgotEl = el('button', { class: 'auth-switch auth-forgot', type: 'button', text: 'Забыли пароль?' });

  // Campurile stau intr-un `<form>`: fara el, telefonul nu propune adresa si parola
  // salvate, iar browserul nu intreaba dupa intrare daca sa le retina.
  // Formularul e construit inaintea ferestrei, iar `trimite` are nevoie de functia care
  // o inchide — deci trece printr-un indirect scurt, umplut mai jos.
  let laTrimitere = () => {};

  const body = el('div', { class: 'auth' }, [
    titleEl, hintEl,
    el('form', {
      class: 'auth-form',
      onsubmit: (e) => { e.preventDefault(); laTrimitere(); },
    }, [userField, emailField, passField, pass2Field, acordField, errEl, submitEl]),
    el('div', { class: 'auth-links' }, [switchEl, forgotEl]),
  ]);

  const render = () => {
    const inreg = regim === 'register';
    const recuperare = regim === 'forgot';

    // Recuperarea cere doar adresa; înregistrarea cere tot; intrarea, nick și parolă.
    userField.style.display = recuperare ? 'none' : '';
    emailField.style.display = inreg || recuperare ? '' : 'none';
    passField.style.display = recuperare ? 'none' : '';
    pass2Field.style.display = inreg ? '' : 'none';
    acordField.style.display = inreg ? '' : 'none';
    forgotEl.style.display = regim === 'login' ? '' : 'none';

    titleEl.textContent = recuperare ? 'Восстановление пароля'
      : inreg ? 'Создать аккаунт' : 'Вход';
    hintEl.textContent = recuperare
      ? 'Укажите почту — пришлём ссылку для смены пароля.'
      : inreg
        ? (session.empty
          ? 'Аккаунтов ещё нет — первый становится владельцем сообщества.'
          : 'Пароль: минимум 6 символов. На почту придёт письмо для подтверждения.')
        : 'Можно войти по нику или по почте, на которую вы регистрировались.';
    submitEl.textContent = recuperare ? 'Прислать ссылку'
      : inreg ? 'Зарегистрироваться' : 'Войти';
    switchEl.textContent = inreg || recuperare ? 'Уже есть аккаунт? Войти' : 'Нет аккаунта? Создать';
    passEl.autocomplete = inreg ? 'new-password' : 'current-password';
    // La înregistrare nick-ul e ceva ce alegi; la intrare e ceva ce poate ai uitat,
    // aşa că acolo acceptăm şi adresa şi o scriem în câmp, nu doar în ajutor.
    userEl.placeholder = inreg ? 'Ник' : 'Ник или почта';
    errEl.textContent = '';
  };

  const trimite = async (close) => {
    errEl.textContent = '';
    submitEl.disabled = true;
    try {
      if (regim === 'forgot') {
        await session.forgot(emailEl.value);
        close();
        // Mesajul e același și dacă adresa nu există: altfel formularul ar spune
        // cine are cont pe platformă.
        toast('Если такая почта есть — письмо отправлено', 'success');
        return;
      }
      if (regim === 'register') {
        // Verificăm aici ce se poate verifica aici: o parolă greşit repetată n-are de ce
        // să ajungă până la server ca să afle omul peste o secundă.
        if (passEl.value !== pass2El.value) throw new Error('Пароли не совпадают');
        if (!acordEl.checked) throw new Error('Нужно принять правила сообщества');
        await session.register(userEl.value.trim(), emailEl.value.trim(), passEl.value);
        close();
        toast(session.user.email_verified
          ? `Привет, ${session.user.username}`
          : 'Аккаунт создан. Проверьте почту и подтвердите адрес.', 'success');
        return;
      }
      await session.login(userEl.value.trim(), passEl.value);
      close();
      toast(`Привет, ${session.user.username}`, 'success');
    } catch (err) {
      errEl.textContent = err.message;
      // La parole nepotrivite cursorul trebuie să ajungă la a doua, nu la prima.
      if (regim === 'register' && err.message.includes('совпад')) pass2El.select();
      else if (regim !== 'forgot') passEl.select();
    } finally {
      submitEl.disabled = false;
    }
  };

  const dlg = modal({ title: 'Сообщество', width: 380, body, actions: [] });

  laTrimitere = () => trimite(dlg.close);
  switchEl.onclick = () => { regim = regim === 'login' ? 'register' : 'login'; render(); };
  forgotEl.onclick = () => { regim = 'forgot'; render(); };
  // „Enter" il trateaza formularul; un ascultator in plus pe fiecare camp ar trimite
  // de doua ori — o data de la tasta, o data de la `submit`.

  render();
  setTimeout(() => (regim === 'forgot' ? emailEl : userEl).focus(), 30);
  return dlg;
}

/** Fereastra de „setează o parolă nouă", deschisă din linkul de recuperare. */
export function openReset(token) {
  const { field: passField, input: passEl } = passwordField('Новый пароль', 'new-password');
  const { field: pass2Field, input: pass2El } = passwordField('Повторите пароль', 'new-password');
  const errEl = el('div', { class: 'auth-error' });

  const dlg = modal({
    title: 'Новый пароль',
    width: 380,
    body: el('div', { class: 'auth' }, [
      el('p', { class: 'auth-hint', text: 'Минимум 6 символов. Все открытые сессии будут закрыты.' }),
      passField, pass2Field, errEl,
    ]),
    actions: [
      { label: 'Отмена', onClick: (c) => c() },
      {
        label: 'Сохранить', primary: true,
        onClick: async (c) => {
          errEl.textContent = '';
          try {
            if (passEl.value !== pass2El.value) throw new Error('Пароли не совпадают');
            await session.resetPassword(token, passEl.value);
            c();
            toast('Пароль изменён — войдите заново', 'success');
            openAuth({ mode: 'login' });
          } catch (err) {
            errEl.textContent = err.message;
          }
        },
      },
    ],
  });
  setTimeout(() => passEl.focus(), 30);
  return dlg;
}
